import { flexRender, type Table } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import type { JobPosting } from "@/services/api/job-postings";

interface JobPostingsTableProps {
  table: Table<JobPosting>;
  isLoading: boolean;
  data: JobPosting[];
  onRowClick?: (posting: JobPosting) => void;
}

export function JobPostingsTable({
  table,
  isLoading,
  data,
  onRowClick,
}: JobPostingsTableProps) {
  if (isLoading) {
    return (
      <div className="py-12 text-center text-sm text-ink-500">Loading...</div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-ink-500">
        No job postings found
      </div>
    );
  }

  return (
    <div className="w-full h-full max-w-full overflow-x-auto overflow-y-auto min-w-0">
      <table className="border-collapse w-full">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr
              key={headerGroup.id}
              className="border-b border-grid-200 bg-white sticky top-0 z-10"
            >
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink-600 whitespace-nowrap cursor-pointer select-none hover:bg-grid-50"
                  style={{ width: header.getSize() }}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <div className="flex items-center gap-1">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    {header.column.getCanSort() && (
                      <span className="text-ink-400">
                        {header.column.getIsSorted() === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : header.column.getIsSorted() === "desc" ? (
                          <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 opacity-40" />
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className="border-b border-grid-100 hover:bg-grid-50 transition-colors cursor-pointer"
              onClick={() => onRowClick?.(row.original)}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className="px-4 py-3 text-sm text-ink-700 whitespace-nowrap"
                  style={{ width: cell.column.getSize() }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
